import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import { ACTIONS, MUTATIONS } from '@/store/namespaces';
import {ArticlesClient, DigitalDocumentsClient, KSeFClient, SupportTicketsClient, UserNotificationsClient, UsersClient} from '@/services/api-clients';
import { NotificationTypeEnum } from '@/enums/notification-type.enum';
import i18n from '@/i18n/config';
import { LF } from '@/i18n/LF';
import { driverChatHub } from '@/services/driver-chat-hub';

export const actions: ActionTree<RootState, RootState> = {
  async [ACTIONS.GET_USER_INFO]({ commit }, payload: boolean): Promise<void> {
    const userClient = payload
      ? new DigitalDocumentsClient().getSignatureDeviceInfo()
      : new UsersClient().getMyInfo();
    return userClient.then((userInfoResult) => {
      if (userInfoResult.success && userInfoResult.result) {
        commit(MUTATIONS.SET_USER_INFO, userInfoResult.result);
      } else {
        commit(MUTATIONS.SHOW_SNACKBAR_MESSAGE, { type: NotificationTypeEnum.Error, content: userInfoResult.errors });
      }
    }).catch(() => {
      commit(MUTATIONS.SHOW_SNACKBAR_MESSAGE, { type: NotificationTypeEnum.Error, content: [i18n.t(LF.UnableToFetchUserDetails)] });
    });
  },

  async [ACTIONS.GET_ARTICLE_GROUPS]({ commit }): Promise<void> {
    return new ArticlesClient().getAllGroups().then((groupsResult) => {
      if (groupsResult.success && groupsResult.result) {
        commit(MUTATIONS.SET_ARTICLE_GROUPS, groupsResult.result);
      } else {
        commit(MUTATIONS.SHOW_SNACKBAR_MESSAGE, { type: NotificationTypeEnum.Error, content: groupsResult.errors });
      }
    }).catch(() => {
      commit(MUTATIONS.SHOW_SNACKBAR_MESSAGE, { type: NotificationTypeEnum.Error, content: [i18n.t(LF.UnableToFetchArticleGroupsDetails)] });
    });
  },

  async [ACTIONS.GET_KSEF_CONFIG]({ commit }): Promise<void> {
    try {
      const ksefConfigResult = await new KSeFClient().config();
      if (ksefConfigResult.success && ksefConfigResult.result) {
        commit(MUTATIONS.SET_KSEF_ENABLED, ksefConfigResult.result.enabled ?? false);
      } else {
        commit(MUTATIONS.SET_KSEF_ENABLED, false);
      }
    } catch {
      commit(MUTATIONS.SET_KSEF_ENABLED, false);
    }
  },

  async [ACTIONS.START_DRIVER_CHAT_HUB](): Promise<void> {
    if (driverChatHub.isConnected()) return;
    try {
      await driverChatHub.start();
    } catch {
      // Sentry already captured inside hub.start(); swallow tutaj zeby nie blokowac load'u apki
    }
  },

  async [ACTIONS.STOP_DRIVER_CHAT_HUB](): Promise<void> {
    await driverChatHub.stop();
  },

  async [ACTIONS.GET_OPEN_SUPPORT_TICKETS_COUNT]({ commit }): Promise<void> {
    try {
      const result = await new SupportTicketsClient().getOpenSupportTicketsCount();
      if (result.success && typeof result.result === 'number') {
        commit(MUTATIONS.SET_OPEN_SUPPORT_TICKETS_COUNT, result.result);
      }
    } catch {
      // Initial state already 0; SignalR pushes will overwrite on next event.
    }
  },

  async [ACTIONS.GET_MESSAGE_NOTIFICATIONS]({ commit }): Promise<void> {
    try {
      const result = await new UserNotificationsClient().getAll();
      if (result.success && result.result) {
        commit(MUTATIONS.SET_MESSAGE_NOTIFICATIONS, result.result);
      }
    } catch {
      // Keep whatever is on the list; the next hub event or reconnect refreshes it.
    }
  },

  async [ACTIONS.CHANGE_MESSAGE_NOTIFICATION_IS_READ]({ commit }, payload: { id: number; state: boolean }): Promise<void> {
    const result = await new UserNotificationsClient().changeIsReaded({ id: payload.id, state: payload.state });
    if (result.success) {
      commit(MUTATIONS.SET_MESSAGE_NOTIFICATION_IS_READ, payload);
    } else {
      commit(MUTATIONS.SHOW_SNACKBAR_MESSAGE, { type: NotificationTypeEnum.Error, content: result.errors });
    }
  },

  async [ACTIONS.CHANGE_ALL_MESSAGE_NOTIFICATIONS_READ]({ commit }): Promise<void> {
    const result = await new UserNotificationsClient().changeAllIsReaded();
    if (result.success) {
      commit(MUTATIONS.SET_ALL_MESSAGE_NOTIFICATIONS_READ);
    } else {
      commit(MUTATIONS.SHOW_SNACKBAR_MESSAGE, { type: NotificationTypeEnum.Error, content: result.errors });
    }
  },
};
